// The keyboard focus walk of the game page (R2052, ui-map-interface.md §9).
//
// Tab walks the six menus while the focus is on the map, and walks the
// focus while a control of the chrome holds a keyboard focus (R2082). This
// module holds the pure halves of that rule, so a test pins them without
// an overlay:
//
// - `onTheMap` says whether a key press starts on the map: the page
//   itself, the root element, a canvas, or no element at all (a press
//   sent to the window).
// - `menusTakeTab` says whether Tab walks the menus: from the map, and
//   from a control that holds a pointer focus (R2082). `watchFocusSource`
//   marks the focus that a pointer press gave.
// - `helmTakesKeys` says whether the helm keys of the map take a press:
//   where Tab walks the menus, but never on a select (R2835).
// - `keepFocus` rewrites a host and puts the focus back on the control
//   that stands in the place of the old one. The dock body is rewritten
//   whenever its markup changes (§10.3), and a rewrite under a focused
//   control would drop the focus on the page, where the next Tab walks
//   the menus and not the controls.
// - `rowTarget` says where the arrow keys move the keyboard focus in the
//   dock body: one row up or down, or to the first or the last row
//   (R2083). It reads the rows off the markup at each press, so it keeps
//   no state that the rewrite of the body could lose.

/** Whether a key press with this target starts on the map. */
export function onTheMap(target: EventTarget | null): boolean {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return true;
  return target === document.body || target === document.documentElement
    || target.tagName === 'CANVAS';
}

/** The control that a pointer press focused, while no other focus came
 *  after it (R2082). */
let pointerFocused: Element | null = null;
/** Whether a pointer press came after the last key press. */
let pointerLast = false;
/** The overlays that watch the focus source. */
let watchers = 0;

const onPointerDown = () => { pointerLast = true; };
const onKeyDown = () => { pointerLast = false; };
const onFocusIn = (e: FocusEvent) => {
  pointerFocused = pointerLast && e.target instanceof Element ? e.target : null;
};

/**
 * Watches where each focus came from (R2082): a focus that follows a
 * pointer press is a pointer focus, and every other focus is not. It
 * returns the call that stops the watch. Chrome sets its keyboard flag on
 * every key press that is no modifier, before the page sees the press, so
 * `:focus-visible` on a clicked button can read true during the Tab press
 * itself. The watch does not depend on that flag.
 */
export function watchFocusSource(): () => void {
  if (watchers++ === 0) {
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('focusin', onFocusIn, true);
  }
  let done = false;
  return () => {
    if (done) return;
    done = true;
    if (--watchers > 0) return;
    window.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('focusin', onFocusIn, true);
    pointerFocused = null;
    pointerLast = false;
  };
}

/**
 * Whether the focus on `el` is a keyboard focus (R2082). A focus that the
 * watch saw a pointer press give is not one. Every other focus reads the
 * browser's `:focus-visible`. Null where the engine refuses the selector,
 * so the caller keeps its old rule there.
 */
export function keyboardFocus(el: Element): boolean | null {
  if (el === pointerFocused) return false;
  try {
    return el.matches(':focus-visible');
  } catch {
    return null;
  }
}

/**
 * Whether `tab` and `shift-tab` walk the menus (R2082, which narrows
 * R2052). They walk the menus from the map, and from a control of the
 * chrome that holds a pointer focus: a press on a button of the dock
 * leaves the focus on it, and the old habit of R543 must survive that
 * press. Only a keyboard focus in the chrome gives the press to the
 * browser's focus walk. Where the engine cannot tell the two apart, the
 * rule of R2052 stays: the map alone walks the menus.
 */
export function menusTakeTab(target: EventTarget | null): boolean {
  if (onTheMap(target)) return true;
  return keyboardFocus(target as Element) === false;
}

/**
 * Whether the helm keys of the map take a press (R2707, R2835). They take
 * it where Tab walks the menus: from the map, and from a control that a
 * pointer press focused. A click on a menu tab leaves the focus on the
 * tab, and the arrows must still steer the selected fleet. A keyboard
 * focus keeps the arrows for the row walk (R2083), and a select keeps
 * them for its own value.
 */
export function helmTakesKeys(target: EventTarget | null): boolean {
  if (typeof Element !== 'undefined' && target instanceof Element && target.tagName === 'SELECT') {
    return false;
  }
  return menusTakeTab(target);
}

/** What names one control across a rewrite: its tag, every `data-*`
 *  attribute, and its rank among the controls that share both. */
interface FocusMark {
  tag: string;
  sig: string;
  rank: number;
}

/** The tag and the `data-*` attributes of a control, as one string. */
function signature(el: Element): string {
  const parts: string[] = [];
  for (const a of Array.from(el.attributes)) {
    if (a.name.startsWith('data-')) parts.push(`${a.name}=${a.value}`);
  }
  return parts.sort().join('&');
}

/** The controls of `host` with this tag and this signature, in order. */
function peers(host: Element, tag: string, sig: string): Element[] {
  return Array.from(host.getElementsByTagName(tag)).filter((e) => signature(e) === sig);
}

/** The mark of a focused control inside `host`. */
function markOf(el: Element, host: Element): FocusMark {
  const tag = el.tagName;
  const sig = signature(el);
  return { tag, sig, rank: Math.max(0, peers(host, tag, sig).indexOf(el)) };
}

/**
 * Runs `write`, which rewrites `host`, and keeps the keyboard focus. A
 * control that survives the rewrite keeps the focus by itself. Otherwise
 * the focus goes to the new control with the same tag, the same `data-*`
 * attributes and the same rank among them. When the new markup holds no
 * such control, the focus stays where the browser put it: on the page.
 *
 * Only a keyboard focus comes back (R2082). The `focus()` call here is a
 * script focus with no pointer press before it, so the watch reads it as
 * no pointer focus, and Chrome gives it `:focus-visible` when a key went
 * down since the last pointer press. A pointer focus put back after a key
 * press would thus turn into a keyboard focus, and the next Tab would walk
 * the chrome. So a pointer focus falls on the page with its control, and
 * Tab walks the menus. A focus that the engine cannot read comes back, as
 * R2052 had it.
 */
export function keepFocus(host: HTMLElement, write: () => void): void {
  const was = document.activeElement;
  if (!(was instanceof HTMLElement) || was === host || !host.contains(was)
    || keyboardFocus(was) === false) {
    write();
    return;
  }
  const mark = markOf(was, host);
  write();
  if (was.isConnected && host.contains(was)) return;
  const list = peers(host, mark.tag, mark.sig);
  const next = list[Math.min(mark.rank, list.length - 1)];
  if (next instanceof HTMLElement) next.focus({ preventScroll: true });
}

/** A move of the row walk (R2083): `↑`, `↓`, `home` and `end`. */
export type RowMove = 'prev' | 'next' | 'first' | 'last';

/** The moves of the two key rows, in the order of their claims. */
export const ROW_MOVES: Readonly<Record<'row_step' | 'row_ends', readonly RowMove[]>> = {
  row_step: ['prev', 'next'],
  row_ends: ['first', 'last'],
};

/** The controls that take the keyboard focus. */
const CONTROLS = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]),'
  + ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The element that groups the controls of one row of the dock body. */
const ROW = '.row';

/** Whether a control stands on the screen. An engine with no
 *  `checkVisibility` (jsdom) reads every control as shown. */
function shown(el: HTMLElement): boolean {
  if (el.closest('[hidden], [inert]')) return false;
  return typeof el.checkVisibility !== 'function' || el.checkVisibility();
}

/**
 * The rows of `body`, each the list of its controls in the order of the
 * markup. A row is the nearest `.row` round a control inside the body,
 * and a control outside every row is a row of its own.
 */
export function rowsOf(body: Element): HTMLElement[][] {
  const rows: HTMLElement[][] = [];
  let last: Element | null = null;
  for (const c of Array.from(body.querySelectorAll<HTMLElement>(CONTROLS))) {
    if (!shown(c)) continue;
    const r = c.closest(ROW);
    const key = r && r !== body && body.contains(r) ? r : c;
    if (key === last) rows[rows.length - 1].push(c);
    else rows.push([c]);
    last = key;
  }
  return rows;
}

/**
 * The control that a row walk moves the keyboard focus to, or null when
 * the walk stops (R2083). `↓` and `↑` go to the next and the previous
 * row, `home` and `end` to the first and the last one. Each lands on the
 * control at the same place in its row as `from`, or on the last control
 * of a shorter row. From a control outside the rows (the menu tab), `↓`
 * and `home` go to the first row and `end` to the last one; `↑` stops.
 * The walk stops at both ends and does not wrap.
 */
export function rowTarget(body: Element, from: Element, move: RowMove): HTMLElement | null {
  const rows = rowsOf(body);
  if (rows.length === 0) return null;
  const i = rows.findIndex((r) => r.includes(from as HTMLElement));
  const col = i < 0 ? 0 : rows[i].indexOf(from as HTMLElement);
  const to = move === 'first' ? 0
    : move === 'last' ? rows.length - 1
      : move === 'next' ? i + 1
        : i < 0 ? -1 : i - 1;
  const row = rows[to];
  return row ? row[Math.min(col, row.length - 1)] : null;
}
